import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PublishDiffSummary } from "@/components/bi/PublishDiffSummary";
import { SharePreview } from "@/components/bi/SharePreview";
import { ShareGateControls } from "@/components/bi/ShareGateControls";
import { toast } from "sonner";
import { Rocket, Share2, Eye, Copy, LayoutTemplate } from "lucide-react";

export function PublishNoteDialog({ open, onOpenChange, dashboardId, note, setNote, onPublish }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="publish-note-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Rocket className="w-5 h-5 text-[color:var(--brand-mint)]" />Publish dashboard</DialogTitle></DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Viewers, shared links and embeds will see this exact version until you publish again.</p>
        <PublishDiffSummary dashboardId={dashboardId} open={open} />
        <Input data-testid="publish-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed? (optional note)" className="bg-[color:var(--surface)]"
          onKeyDown={(e) => e.key === "Enter" && onPublish(note)} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="publish-note-confirm" onClick={() => onPublish(note)} className="bg-[color:var(--brand-mint)] text-white">Publish</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ShareLinkDialog({ open, onOpenChange, publicUrl, shareStats, dashboardId, dashboardName }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Share2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />Share dashboard</DialogTitle></DialogHeader>
        <p className="text-sm text-[color:var(--ink-3)]">Anyone with this link can view a read-only version of this dashboard.</p>
        <div className="flex gap-2 mt-2">
          <Input data-testid="share-link" readOnly value={publicUrl} className="bg-[color:var(--surface)] text-xs" />
          <Button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} className="bg-[color:var(--brand-ocean)] text-white"><Copy className="w-4 h-4" /></Button>
        </div>
        <a href={publicUrl} target="_blank" rel="noreferrer" className="text-sm text-[color:var(--brand-ocean)] mt-1 inline-flex items-center gap-1"><Eye className="w-4 h-4" />Open public view</a>
        {shareStats && (
          <div data-testid="share-stats" className="grid grid-cols-3 gap-2 mt-2">
            {[
              { label: "Total views", value: shareStats.total_views, testid: "share-stat-views" },
              { label: "Unique visitors", value: shareStats.unique_visitors, testid: "share-stat-visitors" },
              { label: "Last 7 days", value: shareStats.views_7d, testid: "share-stat-7d" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 py-2">
                <div className="font-display text-lg text-[color:var(--ink)]">{s.value}</div>
                <div className="text-[11px] text-[color:var(--ink-3)]">{s.label}</div>
              </div>
            ))}
            <div className="col-span-3 text-[11px] text-[color:var(--ink-3)]">
              {shareStats.last_viewed ? `Last viewed ${new Date(shareStats.last_viewed).toLocaleString()}` : "No views recorded yet"}
              {shareStats.embed_views > 0 ? ` · ${shareStats.embed_views} via embeds` : ""}
            </div>
          </div>
        )}
        <SharePreview dashboardName={dashboardName} />
        <ShareGateControls dashboardId={dashboardId} scope="share" />
      </DialogContent>
    </Dialog>
  );
}

export function TemplateSaveDialog({ widget, name, setName, onClose, onSave }) {
  return (
    <Dialog open={!!widget} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="template-save-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-[color:var(--brand-mint)]" />Save widget as template</DialogTitle></DialogHeader>
        <p className="text-xs text-[color:var(--ink-3)]">Templates keep the visual type, dataset binding, fields, formatting and interactions — insert them into any dashboard from the widget library.</p>
        <Input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className="bg-[color:var(--surface)]"
          onKeyDown={(e) => e.key === "Enter" && onSave()} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="template-save-btn" disabled={!name.trim()} onClick={onSave} className="bg-[color:var(--brand-mint)] text-white">Save template</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
